/**
 * Shared founder-dashboard Gate A money (eng D7/D9).
 * Totals and series share a 20s inflight cache so Brief / KPI / Overlay / Pulse
 * do not stampede the same window.
 */
import {
  amazonConnectApi,
  type OrdersMetrics,
  type OrdersMetricsBucket,
  type OrdersMetricsSeries,
} from '../api/amazonconnect';
import type { WindowAggregate } from './daily-brief/aggregateOrders';

export type GateAWindow = {
  from: string;
  to: string;
  tz?: string;
};

const TTL_MS = 45_000;

type CacheEntry<T> = { at: number; data: T };
const metricsCache = new Map<string, CacheEntry<OrdersMetrics>>();
const metricsInflight = new Map<string, Promise<OrdersMetrics>>();
const seriesCache = new Map<string, CacheEntry<OrdersMetricsSeries>>();
const seriesInflight = new Map<string, Promise<OrdersMetricsSeries>>();

function cacheGet<T>(map: Map<string, CacheEntry<T>>, key: string): T | null {
  const hit = map.get(key);
  if (!hit) return null;
  if (Date.now() - hit.at >= TTL_MS) {
    map.delete(key);
    return null;
  }
  return hit.data;
}

export function calendarDay(iso: string, tz = 'Asia/Kolkata'): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(iso));
}

export function hourInTz(iso: string, tz = 'Asia/Kolkata'): number {
  const raw = new Intl.DateTimeFormat('en-GB', {
    timeZone: tz,
    hour: '2-digit',
    hourCycle: 'h23',
  }).format(new Date(iso));
  return Number.parseInt(raw, 10) || 0;
}

export async function fetchOrdersMetrics(
  connectionId: string,
  win: GateAWindow,
): Promise<OrdersMetrics> {
  const tz = win.tz ?? 'Asia/Kolkata';
  const key = `${connectionId}|${win.from}|${win.to}|${tz}`;
  const hit = cacheGet(metricsCache, key);
  if (hit) return hit;
  const pending = metricsInflight.get(key);
  if (pending) return pending;
  const job = amazonConnectApi
    .getOrdersMetrics(connectionId, { from: win.from, to: win.to, tz })
    .then((data) => {
      metricsCache.set(key, { at: Date.now(), data });
      return data;
    })
    .finally(() => {
      metricsInflight.delete(key);
    });
  metricsInflight.set(key, job);
  return job;
}

export async function fetchOrdersMetricsSeries(
  connectionId: string,
  win: GateAWindow & { grain: 'day' | 'hour' },
): Promise<OrdersMetricsSeries> {
  const tz = win.tz ?? 'Asia/Kolkata';
  const key = `${connectionId}|${win.from}|${win.to}|${tz}|${win.grain}`;
  const hit = cacheGet(seriesCache, key);
  if (hit) return hit;
  const pending = seriesInflight.get(key);
  if (pending) return pending;
  const job = amazonConnectApi
    .getOrdersMetricsSeries(connectionId, {
      from: win.from,
      to: win.to,
      tz,
      grain: win.grain,
    })
    .then((data) => {
      const normalized: OrdersMetricsSeries = {
        ...data,
        buckets: data.buckets ?? [],
      };
      seriesCache.set(key, { at: Date.now(), data: normalized });
      return normalized;
    })
    .finally(() => {
      seriesInflight.delete(key);
    });
  seriesInflight.set(key, job);
  return job;
}

/** Map Gate A totals onto the dashboard aggregate shape (same math as Yesterday). */
export function windowFromMetrics(met: OrdersMetrics): WindowAggregate {
  const items = Number(met.order_items) || 0;
  const sales = Number(met.sales) || 0;
  const units = Number(met.units) || 0;
  return {
    orderCount: items,
    ordersWithTotal: items,
    sales,
    currencies: met.currency ? [met.currency] : [],
    units,
    ordersWithUnits: items,
    unshipped: 0,
    canceled: 0,
    aov: items > 0 ? sales / items : sales === 0 ? 0 : null,
  };
}

export function windowFromBucket(b: OrdersMetricsBucket, currency?: string): WindowAggregate {
  return windowFromMetrics({
    connection_id: '',
    from: b.start,
    to: b.start,
    timezone: 'Asia/Kolkata',
    sales: b.sales,
    order_items: b.order_items,
    units: b.units,
    complete: true,
    currency,
  });
}

export function windowFromSeriesDays(
  buckets: OrdersMetricsBucket[],
  fromIso: string,
  toIso: string,
  tz = 'Asia/Kolkata',
  currency?: string,
): WindowAggregate {
  let sales = 0;
  let items = 0;
  let units = 0;
  let ccy = currency;
  for (const b of buckets) {
    const d = calendarDay(b.start, tz);
    if (d < fromIso || d > toIso) continue;
    sales += Number(b.sales) || 0;
    items += Number(b.order_items) || 0;
    units += Number(b.units) || 0;
  }
  return windowFromMetrics({
    connection_id: '',
    from: fromIso,
    to: toIso,
    timezone: tz,
    sales,
    order_items: items,
    units,
    complete: true,
    currency: ccy,
  });
}

export function dayAggMap(
  buckets: OrdersMetricsBucket[],
  tz = 'Asia/Kolkata',
  currency?: string,
): Record<string, WindowAggregate> {
  const out: Record<string, WindowAggregate> = {};
  for (const b of buckets) {
    out[calendarDay(b.start, tz)] = windowFromBucket(b, currency);
  }
  return out;
}
